/**
 * OrderingQuiz Adult Renderer
 * Clean drag-and-drop reorderable items using @dnd-kit.
 * Uses the useOrderingQuiz headless hook for all logic.
 */

import { forwardRef, useCallback, useState } from 'react'
import type { ComponentPropsWithoutRef } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { cn } from '../../../lib/utils'
import type { QuizSolveResult } from '../../../types/legacyQuiz'
import { formatText } from '../../../utils/quiz-parser'
import type { OrderingQuizData, OrderingChoice } from '../hooks'
import { useOrderingQuiz } from '../hooks'
import { KatexText, QuizResultFeedback, SubmitButton, QuizSolveDescription, QuizMedia, QuizTypeBadge } from '../shared'
import {
  ORDERING_DROP_ANIMATION,
  ORDERING_MOUSE_ACTIVATION_CONSTRAINT,
  ORDERING_TOUCH_ACTIVATION_CONSTRAINT,
  reorderOrderingChoices,
} from '../ordering-dnd'

// ──────────────────────────────────────────────
// Axis restriction modifiers
// ──────────────────────────────────────────────

const restrictToHorizontal = ({ transform }: { transform: { x: number; y: number; scaleX: number; scaleY: number } }) => ({
  ...transform,
  y: 0,
})

const restrictToVertical = ({ transform }: { transform: { x: number; y: number; scaleX: number; scaleY: number } }) => ({
  ...transform,
  x: 0,
})

// ──────────────────────────────────────────────
// SortableItem Sub-component (Adult style)
// ──────────────────────────────────────────────

interface OrderingAdultCardProps extends Omit<ComponentPropsWithoutRef<'div'>, 'children'> {
  answer: OrderingChoice
  index: number
  isCorrect?: boolean | null
  isSolved: boolean
  isDragging: boolean
  isOverlay?: boolean
}

const OrderingAdultCard = forwardRef<HTMLDivElement, OrderingAdultCardProps>(
  function OrderingAdultCard(
    {
      answer,
      index,
      isCorrect,
      isSolved,
      isDragging,
      isOverlay = false,
      className,
      ...props
    },
    ref,
  ) {
    return (
      <div
        ref={ref}
        className={cn(
          'relative flex items-center gap-3 p-4 rounded-xl border-2 bg-white transition-all',
          'touch-manipulation select-none',
          isDragging && 'border-blue-400 bg-blue-50 shadow-lg z-10',
          isDragging && !isOverlay && 'opacity-30',
          !isDragging && !isSolved && 'border-gray-200 hover:border-blue-300 hover:shadow-2xs cursor-grab',
          isSolved && isCorrect === true && 'border-green-300 bg-green-50',
          isSolved && isCorrect === false && 'border-red-300 bg-red-50',
          isSolved && 'pointer-events-none',
          className,
        )}
        {...props}
      >
        {/* Number badge */}
        <div className={cn(
          'shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold',
          isDragging && 'bg-blue-500 text-white',
          !isDragging && !isSolved && 'bg-blue-500 text-white',
          isSolved && isCorrect === true && 'bg-green-500 text-white',
          isSolved && isCorrect === false && 'bg-red-500 text-white',
        )}>
          {index + 1}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {answer.image && (
            <img
              src={answer.image.signedUrl || answer.image.url}
              alt={`item-${index}`}
              className="max-h-20 w-auto object-contain"
            />
          )}
          {answer.answer && (
            <span className="text-lg font-medium text-gray-800">
              <KatexText text={answer.answer} />
            </span>
          )}
        </div>
      </div>
    )
  },
)

interface SortableItemProps {
  id: string
  answer: OrderingChoice
  index: number
  isCorrect?: boolean | null
  isSolved: boolean
}

function SortableItem({ id, answer, index, isCorrect, isSolved }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 99 : ('auto' as const),
  }

  return (
    <OrderingAdultCard
      ref={setNodeRef}
      style={style}
      answer={answer}
      index={index}
      isCorrect={isCorrect}
      isSolved={isSolved}
      isDragging={isDragging}
      {...attributes}
      {...listeners}
    />
  )
}

// ──────────────────────────────────────────────
// Main Component
// ──────────────────────────────────────────────

interface OrderingQuizProps {
  quizId: string | number
  quiz: OrderingQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  submitLabel?: string
  hideSubmitButton?: boolean
  onAnswerChange?: (answers: string[]) => void
  disableSound?: boolean
  difficultyLevel?: number | null
  /** G3 — true면 풀이 후 해설(QuizSolveDescription)을 숨긴다(정오답 피드백은 유지). 기본 false=현행 노출. */
  hideExplanation?: boolean
}

export function OrderingAdult({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel,
  hideExplanation = false,
}: OrderingQuizProps) {
  const { t } = useTranslation('quiz')
  const [activeId, setActiveId] = useState<string | null>(null)

  const {
    orderedChoices,
    setOrderedChoices,
    isVertical,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleSubmit,
  } = useOrderingQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange,
  })

  // DnD sensors
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: ORDERING_MOUSE_ACTIVATION_CONSTRAINT,
    }),
    useSensor(TouchSensor, {
      activationConstraint: ORDERING_TOUCH_ACTIVATION_CONSTRAINT,
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id))
  }, [])

  const handleDragCancel = useCallback(() => {
    setActiveId(null)
  }, [])

  // Drag end handler
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)
    if (!over || active.id === over.id) return

    setOrderedChoices(prev => {
      return reorderOrderingChoices(prev, String(active.id), String(over.id))
    })
  }, [setOrderedChoices])

  const isDisabled = isSolved || isViewSolved
  const activeIndex = activeId === null
    ? -1
    : orderedChoices.findIndex(choice => choice.key === activeId)
  const activeChoice = activeIndex >= 0 ? orderedChoices[activeIndex] : null

  // Arrow color (adult Tailwind)
  const getArrowColor = () => {
    if (!isSolved) return 'text-blue-400'
    if (solveResult?.correct || shouldShowCorrectAnswers) return 'text-green-500'
    return 'text-red-500'
  }

  return (
    <div className="space-y-6">
      {/* Quiz type badge */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-2"
      >
        <QuizTypeBadge
          quizId={quizId}
          typeLabel={t('types.ordering.label')}
          difficultyLevel={difficultyLevel}
        />
      </motion.div>

      {/* Title + problem + media */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className={cn(
          'p-6 bg-linear-to-br to-gray-50 rounded-2xl from-slate-50',
          'border border-gray-100 shadow-2xs',
          'text-2xl font-medium leading-relaxed text-gray-900'
        )}
      >
        <KatexText text={quiz.title} />
        {quiz.problem && (
          <div className="mt-4 text-xl">
            {formatText(quiz.problem, `${quizId}-problem`, quiz.additionalImages ?? undefined)}
          </div>
        )}
        <QuizMedia image={quiz.image} imageMobile={quiz.imageMobile} desmos={quiz.desmos} vars={quiz.vars} />
      </motion.div>

      {/* Drag instruction */}
      {!isDisabled && (
        <p className="text-sm text-gray-500">
          {t('types.ordering.dragInstruction')}
        </p>
      )}

      {/* DnD sortable list */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragCancel={handleDragCancel}
        onDragEnd={handleDragEnd}
        modifiers={isVertical ? [restrictToVertical] : [restrictToHorizontal]}
      >
        <SortableContext
          items={orderedChoices.map(c => c.key)}
          strategy={isVertical ? verticalListSortingStrategy : horizontalListSortingStrategy}
        >
          <div className={cn(
            isVertical
              ? 'flex flex-col items-stretch space-y-3'
              : 'flex flex-row items-center justify-between space-x-4'
          )}>
            {orderedChoices.map((choice, index) => {
              const isCorrect = isSolved
                ? (shouldShowCorrectAnswers || (solveResult?.correct ?? false))
                : null

              return (
                <div
                  key={choice.key}
                  className="relative"
                  style={!isVertical ? { width: `${100 / orderedChoices.length}%` } : undefined}
                >
                  {/* Arrow between items */}
                  {index > 0 && (
                    <ArrowRight
                      strokeWidth={2.5}
                      className={cn(
                        'absolute w-5 h-5',
                        getArrowColor(),
                        isVertical
                          ? 'left-1/2 -translate-x-1/2 -top-4 rotate-90'
                          : '-left-3 top-1/2 -translate-y-1/2'
                      )}
                    />
                  )}
                  <SortableItem
                    id={choice.key}
                    answer={choice}
                    index={index}
                    isCorrect={isCorrect}
                    isSolved={isSolved}
                  />
                </div>
              )
            })}
          </div>
        </SortableContext>
        <DragOverlay dropAnimation={ORDERING_DROP_ANIMATION}>
          {activeChoice ? (
            <OrderingAdultCard
              answer={activeChoice}
              index={activeIndex}
              isSolved={isSolved}
              isDragging
              isOverlay
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Submit button */}
      {!isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          label={submitLabel}
        />
      )}

      {/* Result feedback */}
      {isSolved && (
        <QuizResultFeedback
          isCorrect={solveResult?.correct ?? false}
          show={isSolved}
          correctAnswer={shouldShowCorrectAnswers && solveResult?.correctAnswers
            ? solveResult.correctAnswers
              .map(key => quiz.choices.find(c => c.key === key)?.answer)
              .filter(Boolean)
              .join(' → ')
            : undefined}
          disableSound={disableSound}
          isViewSolved={isViewSolved}
        />
      )}

      {/* Solve description */}
      {isSolved && !hideExplanation && (
        <QuizSolveDescription
          description={solveResult?.description ?? undefined}
          commentaryImages={quiz.commentaryImages}
          vars={quiz.vars}
          id={`${quizId}-desc`}
          actionVisible={!solveResult?.correct}
          actionLabel={isShowingCorrectAnswers ? t('common.viewMyAnswer') : t('common.viewCorrectAnswer')}
          onActionClick={() => setIsShowingCorrectAnswers(v => !v)}
        />
      )}
    </div>
  )
}
